import { singleline } from "@core/foundation/string";
import { mcpApi } from "@core/ports/mcp";
import { z } from "@core/zod";

import { PROJECT_DOMAIN } from "@platform/core";

import { MEMORY_SERVICE } from "../config/index.ts";
import {
  AgentSession,
  Memory,
  MemoryIdSchema,
  Project,
  ProjectIdSchema,
  RevisionIdSchema,
  Trigger,
  TriggerIdSchema,
} from "../models/index.ts";

const MemoryInputSchema = z.object(
  {
    text: z.string(),
    title: z.string(),
    triggers: z.array(
      Trigger.createSchema.omit({
        memoryId: true,
        principalId: true,
        projectId: true,
      }),
    ),
  },
  {
    text: "The memory's text, injected when a trigger fires",
    title: "A short title naming the memory",
    triggers: singleline`
      The triggers the memory fires on, each holding its condition. A file
      condition holds any of a picomatch glob, dotfiles included, on the file's
      path relative to the repository root, a JavaScript regexp on its content,
      and an ast-grep rule on its syntax, all of which have to hold; with
      changeOnly it fires on a change alone and the content parts see the
      changed lines, otherwise it fires on a read or a change against the whole
      file. A toolCall condition holds a JavaScript regexp on the called tool's
      name, and a bashCommand condition one on the command; each may set
      blocking to make the memory block the call, denied and injected before it
      runs so that it is replanned and retried, where it otherwise follows the
      call, injected beside its result. Every regexp compiles with the u flag,
      so a pattern that does not compile under it is refused. A memory that
      fires on either of two globs or rules takes one trigger each
    `,
  },
);

export type MemoryInput = z.infer<typeof MemoryInputSchema>;

const MemoryCreationResultSchema = z.object(
  {
    memoryId: MemoryIdSchema,
    revisionId: RevisionIdSchema,
    triggerIds: z.array(TriggerIdSchema),
  },
  {
    memoryId: "The id of the memory created",
    revisionId: "The id of the memory's first revision",
    triggerIds: "The ids of the memory's triggers",
  },
);

export type MemoryCreationResult = z.infer<typeof MemoryCreationResultSchema>;

export const memoryMcpApi = mcpApi(
  {
    domain: `mcp.${MEMORY_SERVICE.LABEL}.${PROJECT_DOMAIN}`,
    scopes: ["memory"],
    serverInfo: { name: MEMORY_SERVICE.LABEL, version: "0.1.0" },
  },
  {
    archive_memory: {
      description: singleline`
        Archives a memory in one of your projects, so it is no longer served for
        injection. Its records stay whole, and unarchive_memory serves it again.
        The memory records the archive with your reason as its last status
        change, and a memory already archived is refused with who changed its
        status last and why. Returns the memory.
      `,
      inputSchema: z.object(
        {
          memoryId: MemoryIdSchema,
          projectId: ProjectIdSchema,
          reason: z.string(),
        },
        {
          memoryId: "The id of the memory to archive",
          projectId: "The id of the project the memory belongs to",
          reason: singleline`
            Why the memory is archived, which the owner reads when reviewing the
            change
          `,
        },
      ),
      resultSchema: z.object(
        { memory: Memory.schema },
        { memory: "The memory" },
      ),
    },
    create_memories: {
      description: singleline`
        Creates several memories in one of your projects in one transaction,
        each as create_memory creates one, so either every memory is created or
        none is. Returns the ids of each memory, its revision, and its triggers,
        in the input's order. The transaction holds a hundred items, two per
        memory, one per trigger, and one for the project, and a call exceeding
        that is refused before anything is written.
      `,
      inputSchema: z.object(
        { memories: z.array(MemoryInputSchema), projectId: ProjectIdSchema },
        {
          memories: singleline`
            The memories to create, in order, each of the shape create_memory
            takes
          `,
          projectId: "The id of the project the memories belong to",
        },
      ),
      resultSchema: z.object(
        { memories: z.array(MemoryCreationResultSchema) },
        {
          memories: singleline`
            The ids of each memory created, its revision, and its triggers, in
            the input's order
          `,
        },
      ),
    },
    create_memory: {
      description: singleline`
        Creates a memory in one of your projects: its text as the memory's first
        revision, and one trigger per condition the memory fires on. Returns the
        ids of the memory, the revision, and the triggers.
      `,
      inputSchema: z.object(
        { ...MemoryInputSchema.shape, projectId: ProjectIdSchema },
        {
          ...MemoryInputSchema.descriptions,
          projectId: "The id of the project the memory belongs to",
        },
      ),
      resultSchema: MemoryCreationResultSchema,
    },
    get_agent_session: {
      description: singleline`
        Returns an agent session of one of your projects with a link to its
        whole event log: one JSON line per event, in the order the events
        happened. The log is rebuilt on every call, and an event reaches it up
        to about 22 minutes after it happened, and the link expires after 12
        hours.
      `,
      inputSchema: z.object(
        { projectId: ProjectIdSchema, sessionId: z.safeId() },
        {
          projectId: "The id of the project the session belongs to",
          sessionId: "The id the agent host gave the session",
        },
      ),
      resultSchema: z.object(
        {
          session: AgentSession.schema,
          sizeBytes: z.int().nonnegative(),
          url: z.string(),
        },
        {
          session: "The agent session",
          sizeBytes: "The size of the session's event log, in bytes",
          url: "The link the event log downloads from, expiring after 12 hours",
        },
      ),
    },
    list_projects: {
      description:
        "Returns your projects, each with the id the create tools take.",
      inputSchema: z.object({}),
      resultSchema: z.object(
        { projects: z.array(Project.schema) },
        { projects: "Your projects" },
      ),
    },
    unarchive_memory: {
      description: singleline`
        Unarchives an archived memory in one of your projects, so it is served
        for injection again. The memory records the unarchive with your reason
        as its last status change, and a memory already active is refused with
        who changed its status last and why. Returns the memory.
      `,
      inputSchema: z.object(
        {
          memoryId: MemoryIdSchema,
          projectId: ProjectIdSchema,
          reason: z.string(),
        },
        {
          memoryId: "The id of the memory to unarchive",
          projectId: "The id of the project the memory belongs to",
          reason: singleline`
            Why the memory is unarchived, which the owner reads when reviewing
            the change
          `,
        },
      ),
      resultSchema: z.object(
        { memory: Memory.schema },
        { memory: "The memory" },
      ),
    },
  },
);
